import { Checkbox, Flex, Grid } from "@eworkit/iris";
import { Formik, FormikProps } from "formik";
import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { FormikError, FormikTextInput, VALIDATION_TYPES } from "@/components/Formik";
import { Group } from "@/components/Group";
import { Text } from "@/components/Text";
import { IAddress, ICompany } from "@/data/companies";
import { FormikForm } from "@/formikComponents";
import { useToggle } from "@/hooks/Toggle";
import { FormikTypeaheadLocationHints } from "@/views/components/Formik";

import { ICompanyFormikProps } from "../../CompanyData/types";

const citySchema = Yup.object().shape({
  city: Yup.string().required(VALIDATION_TYPES.REQUIRED),
  country: Yup.string().required(VALIDATION_TYPES.REQUIRED),
  name: Yup.string().required(VALIDATION_TYPES.REQUIRED),
  locationId: Yup.string().required(VALIDATION_TYPES.REQUIRED),
  countryCode: Yup.string().required(VALIDATION_TYPES.REQUIRED),
  suggestedPhoneCode: Yup.string().nullable(),
});

const addressSchema = Yup.object().shape({
  addressLine1: Yup.string().required(VALIDATION_TYPES.REQUIRED),
  addressLine2: Yup.string().optional(),
  zipCode: Yup.string().required(VALIDATION_TYPES.REQUIRED),
  city: citySchema.nullable().required(VALIDATION_TYPES.REQUIRED),
  state: Yup.string(),
  type: Yup.string().required(VALIDATION_TYPES.REQUIRED),
});

const companyAddressSchema = Yup.array().of(addressSchema);

interface IAddressSectionProps extends IEditableSectionRenderProps {
  source: ICompany;
  form: FormikProps<IAddress[]>;
}

export const AddressSectionForm: React.FC<IAddressSectionProps> = memo(({ editableProps, form }) => {
  useEditableSubmit(form, editableProps);
  const { t } = useTranslation("session");
  const [sameAsPostalToggled, setSameAsPostalToggled] = useToggle(false);

  const handleCheckboxChange = () => {
    setSameAsPostalToggled();

    const postalIndex = form.values.findIndex(address => address.type === "POSTAL");
    const invoiceIndex = postalIndex === 1 ? 0 : 1;

    const postalAddress = form.values[postalIndex];
    const oldInvoiceAddress = form.values[invoiceIndex];
    const newInvoiceAddress = { ...postalAddress, id: oldInvoiceAddress.id, type: "INVOICE" };

    form.setFieldValue(`[${invoiceIndex}]`, newInvoiceAddress);
  };

  return (
    <FormikForm>
      <Grid columns="1fr 1fr" gap="lg">
        {form.values &&
          form.values.map((address, index) => (
            <Flex key={index} flexDirection="column" gap="sm">
              <Group label={t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.ADDRESS.ADDRESS_LINE_1")} direction="column">
                <FormikTextInput type="text" name={`[${index}].addressLine1`} />
                <FormikError name={`[${index}].addressLine1`} data-test="error-addressLine1" />
              </Group>

              <Group label={t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.ADDRESS.ADDRESS_LINE_2")} direction="column">
                <FormikTextInput type="text" name={`[${index}].addressLine2`} />
                <FormikError name={`[${index}].addressLine2`} data-test="error-addressLine2" />
              </Group>

              <Group label={t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.ADDRESS.ZIP_CODE")} direction="column">
                <FormikTextInput type="text" name={`[${index}].zipCode`} />
                <FormikError name={`[${index}].zipCode`} data-test="error-zipCode" />
              </Group>

              <Group label={t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.ADDRESS.STATE")} direction="column">
                <FormikTextInput type="text" name={`[${index}].state`} />
                <FormikError name={`[${index}].state`} data-test="error-state" />
              </Group>

              <Group label={t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.ADDRESS.CITY")} direction="column">
                <FormikTypeaheadLocationHints
                  required
                  size="md"
                  valueKey="name"
                  labelKey="name"
                  name={`[${index}].city`}
                  value={form.values?.[index]?.city}
                />
                <FormikError name={`[${index}].city`} data-test="error-city" />
              </Group>

              <Group label={t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.ADDRESS.COUNTRY")} direction="column">
                <FormikTextInput disabled type="text" name={`[${index}].city.country`} />
                <FormikError name={`[${index}].city.country`} data-test="error-country" />
              </Group>

              {address?.type === "INVOICE" && (
                <Flex flex={1} justifyContent="flex-start" mt="sm" alignItems="center" gap="xs">
                  <Checkbox checked={sameAsPostalToggled} onChange={handleCheckboxChange} />
                  <Text size="md">{t("USERS.SETTINGS.COMPANY_CONTACT_DETAILS.FORM.ADDRESS.SAME_AS_POSTAL")}</Text>
                </Flex>
              )}
            </Flex>
          ))}
      </Grid>
    </FormikForm>
  );
});

interface IGeneralFormikProps
  extends Omit<ICompanyFormikProps<IAddress[]>, "validationSchema">,
    Omit<IAddressSectionProps, "form"> {}

export const CompanyAddressEdit = ({ initialValues, source, onSubmit, onCancel, ...rest }: IGeneralFormikProps) => (
  <Formik
    enableReinitialize
    onSubmit={onSubmit}
    onCancel={onCancel}
    initialValues={[...initialValues]}
    validationSchema={companyAddressSchema}>
    {(form: FormikProps<IAddress[]>) => <AddressSectionForm {...rest} source={source} form={form} />}
  </Formik>
);
